import PropTypes from 'prop-types';
import { format, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from "src/@/components/ui/button";
import { Label } from "src/@/components/ui/label";
import { Calendar } from "src/@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/@/components/ui/popover";
import Iconify from './Iconify';
import { useState, useEffect } from 'react';
import { cn } from "src/@/lib/utils";

MenuDatas2.propTypes = {
  children: PropTypes.node,
  inicial: PropTypes.instanceOf(Date),
  final: PropTypes.instanceOf(Date),
  changeInicial: PropTypes.func.isRequired,
  changeFinal: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  onPesquisar: PropTypes.func.isRequired,
  content: PropTypes.node,
};

export default function MenuDatas2({
  inicial,
  final,
  changeInicial,
  changeFinal,
  onSelect,
  onPesquisar,
  content,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const start = startOfMonth(new Date());
    const end = endOfMonth(new Date());
    changeInicial(start);
    changeFinal(end);
    onPesquisar(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 justify-start text-left font-normal px-3 border-slate-200 shadow-sm hover:bg-slate-50 min-w-[180px]",
            !inicial && "text-muted-foreground"
          )}
        >
          <Iconify className="mr-2 h-3.5 w-3.5 text-slate-500" icon="solar:calendar-date-bold-duotone" />
          <span className="text-xs text-slate-700">
            {inicial && final ? (
              <>
                {format(inicial, 'dd MMM', { locale: ptBR })} - {format(final, 'dd MMM', { locale: ptBR })}
                <span className="ml-1 text-slate-400 font-normal hidden sm:inline-block">({format(inicial, 'yyyy')})</span>
              </>
            ) : (
              content || 'Selecione o período'
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex flex-col sm:flex-row">
          {/* Sidebar Presets */}
          <div className="flex flex-col gap-1 p-2 border-b sm:border-b-0 sm:border-r border-slate-100 min-w-[140px] bg-slate-50/50">
            <span className="px-2 py-1.5 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
              Período Rápido
            </span>
            {[
              { label: 'Esta Semana', getValue: () => [startOfWeek(new Date(), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 })] },
              { label: 'Semana Passada', getValue: () => [startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 })] },
              { label: 'Este Mês', getValue: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
              { label: 'Mês Passado', getValue: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
              { label: 'Últimos 7 dias', getValue: () => [subDays(new Date(), 6), new Date()] },
              { label: 'Últimos 30 dias', getValue: () => [subDays(new Date(), 29), new Date()] },
            ].map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="justify-start text-xs font-medium h-7 px-2 text-slate-600 hover:text-primary hover:bg-white"
                onClick={() => {
                  const [start, end] = preset.getValue();
                  changeInicial(start);
                  changeFinal(end);
                  if (onSelect) onSelect(null);
                  onPesquisar(start, end, null);
                  setOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Custom Range */}
          <div className="p-3 flex flex-col gap-4 min-w-[260px]">
            <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
              Personalizado
            </span>
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label className="text-[11px] text-slate-500 font-medium">Data Inicial</Label>
                <DatePickerInput
                  value={inicial}
                  onChange={changeInicial}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-[11px] text-slate-500 font-medium">Data Final</Label>
                <DatePickerInput
                  value={final}
                  onChange={changeFinal}
                />
              </div>

              <Button
                onClick={() => {
                  onPesquisar();
                  setOpen(false);
                }}
                className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl h-8 mt-2 shadow-sm font-medium transition-all hover:shadow-md active:scale-95 text-xs"
              >
                Pesquisar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DatePickerInput({ value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "w-full justify-start text-left font-normal h-9 px-3 text-sm",
            !value && "text-muted-foreground"
          )}
        >
          <Iconify icon="solar:calendar-date-bold-duotone" className="mr-2 h-4 w-4 opacity-50" />
          {value ? format(value, "dd/MM/yyyy") : <span>Selecione...</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
          initialFocus
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );
}



